import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import PlansGrid from "./PlansGrid";
import PaymentMethods from "./PaymentMethods";
import UsageLimitTab from "./UsageLimitTab";
import Table from "../../../shared/components/common/Table";
import StatusBadge from "../../../shared/components/common/StatusBadge";
import DownloadIcon from "../../../assets/icons/DownloadIcon.svg";
import { PageHeader } from "../../../shared/components/common";
import { useBillingInvoices } from "../hooks/useBillingInvoices";
import { isViewer } from "../../../shared/utils/roles";

const Billing = () => {
  const currentUser = useSelector(selectCurrentUser);
  const viewer = isViewer(currentUser?.user_role);

  const [activeTab, setActiveTab] = useState(viewer ? "usage" : "subscription");
  const {
    currentPage,
    setCurrentPage,
    invoices,
    totalInvoices,
    isInvoicesLoading,
    invoicesError,
  } = useBillingInvoices()

  useEffect(() => {
    if (viewer && activeTab !== "usage") {
      setActiveTab("usage");
    }
  }, [activeTab, viewer]);

  const [showInvoicesError, setShowInvoicesError] = useState(false);
  useEffect(() => {
    if (!invoicesError) {
      setShowInvoicesError(false);
      return;
    }

    setShowInvoicesError(true);
    const timer = setTimeout(() => setShowInvoicesError(false), 5000);
    return () => clearTimeout(timer);
  }, [invoicesError]);

  const columns = useMemo(
    () => [
      {
        Header: 'Invoice ID',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({ value }) => <span className="text-content text-sm">{value}</span>,
      },
      {
        Header: 'Date',
        accessor: 'date',
        disableSortBy: true,
        Cell: ({ value }) => <span className="text-content text-sm">{value}</span>,
      },
      {
        Header: 'Type',
        accessor: 'plan',
        disableSortBy: true,
        Cell: ({ value }) => <span className="text-content text-sm">{value}</span>,
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        disableSortBy: true,
        Cell: ({ value }) => <span className="text-content text-sm">{value}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.preventDefault()
              if (row?.original?.invoiceFileUrl) {
                window.open(row.original.invoiceFileUrl, '_blank', 'noopener,noreferrer')
              }
            }}
            disabled={!row?.original?.invoiceFileUrl}
            className="text-xs sm:text-sm text-primary disabled:text-muted font-semibold flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <img
              src={DownloadIcon} alt="download" className="w-3 h-3" loading="eager"
            />
            Download
          </button>
        ),
      },
    ],
    []
  )

  return (
    <div className="overflow-y-auto">
      <PageHeader
        title="Billing"
        subtitle="Manage your subscription, usage limits, and payment methods."
      />

      <div className="sm:mx-2 mb-4 border border-line rounded-xl bg-white px-3 py-2 shadow-sm">
        <Tabs
          value={activeTab}
          onChange={(_, nextValue) => setActiveTab(nextValue)}
          variant="scrollable"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
        >
          {!viewer && <Tab value="subscription" label="Subscription & Payments" />}
          <Tab value="usage" label="Usage Limit" />
        </Tabs>
      </div>

      {activeTab === "subscription" && !viewer ? (
        <>
          <PlansGrid />
          <PaymentMethods />

          {/* Invoice History Section */}
          <div className="sm:mx-2 my-4 border border-line rounded-t-xl shadow-sm p-4 bg-white">
            <div className="mb-5">
              <h3 className="text-xl lg:text-2xl font-semibold">Invoice History</h3>
              <p className="text-sm text-gray-500">View and download your past invoices</p>
              {showInvoicesError && invoicesError && <p className="text-sm text-red-600 mt-2">
                {/* {invoicesError} */}
                Error loadng invoice history!
                </p>}
            </div>

            <div className="logs-table-area border border-line rounded-lg ">
              {
                invoices.length === 0 ? 
                <p className="p-3 text-sm text-mutedDark">No invoice history available to display.</p>
                : 
              <Table
                columns={columns}
                data={invoices}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                showTotalRecords={true}
                totalRecords={totalInvoices}
                showPageSizeSelector={true}
                useMUIcons={false}
                loading={isInvoicesLoading}
              />
               }
            </div>
          </div>
        </>
      ) : (
        <UsageLimitTab />
      )}
    </div>
  );
};

export default Billing;
